import React, { useEffect, useRef, useState } from 'react'
import { format } from 'date-fns'
import { Calendar as CalendarIcon, Check, Loader2, Package, Trophy, Upload, X } from 'lucide-react'
import { useCampaign } from '@/hooks/useCampaign'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import { GiftingBonusEditor } from '@/components/gifting/GiftingBonusEditor'
import { LogoCropModal } from '@/components/campaign/LogoCropModal'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/components/ui/use-toast'
import { brandsApi, type Brand } from '@/lib/api/brands'
import { cn } from '@/lib/utils'
import {
  CAMPAIGN_TYPES,
  PLATFORMS,
  TONES,
  type CampaignType,
} from '@/components/campaign/wizard/options'
import {
  Chip,
  Counter,
  FieldLabel,
  WizardCard,
  WizardFooter,
} from '@/components/campaign/wizard/WizardParts'

const BrandMark: React.FC<{ brand: Pick<Brand, 'name' | 'logo'> | null; size?: string }> = ({
  brand,
  size = 'h-7 w-7 text-xs',
}) =>
  brand?.logo ? (
    <img src={brand.logo} alt="" className={cn('shrink-0 rounded-md object-cover', size)} />
  ) : (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-md bg-[#0EA5E9] font-bold text-foreground',
        size,
      )}
      style={{ color: '#fff' }}
    >
      {(brand?.name ?? '?')
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()}
    </span>
  )

const DateField: React.FC<{
  label: string
  value: string | null
  onChange: (iso: string | null) => void
  min?: string | null
}> = ({ label, value, onChange, min }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="min-w-0">
      <FieldLabel>{label}</FieldLabel>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              'w-full justify-start bg-card font-normal',
              !value && 'text-muted-foreground',
            )}
          >
            <CalendarIcon className="h-4 w-4" />
            {value ? format(new Date(value), 'MMM d, yyyy') : 'Pick a date'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value ? new Date(value) : undefined}
            onSelect={(date) => {
              onChange(date ? date.toISOString() : null)
              setOpen(false)
            }}
            disabled={(date) => (min ? date < new Date(min) : false)}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

const Step1Basics: React.FC = () => {
  const { campaignData, updateBasics, goToNextStep } = useCampaign()
  const { marketer } = useAuth()
  const org = (marketer as any)?.org
  const { basics } = campaignData
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [cropUrl, setCropUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [brands, setBrands] = useState<Brand[]>([])
  const [loadingBrands, setLoadingBrands] = useState(true)
  const selectedBrand = brands.find((b) => b.name === basics.brand) ?? null
  const [imageSource, setImageSource] = useState<'brandLogo' | 'customPhoto'>(
    basics.logoUrl && basics.logoUrl !== selectedBrand?.logo ? 'customPhoto' : 'brandLogo',
  )
  // Older campaigns could be "content"; new ones pick one of the three
  const type: CampaignType = basics.dealType === 'content' ? 'gifting' : basics.dealType
  const typeInfo = CAMPAIGN_TYPES.find((t) => t.id === type)!
  const dateType = basics.dateType ?? 'exact'

  useEffect(() => {
    brandsApi
      .getBrands()
      .then((res) => {
        setBrands(res.data)
        // One brand: pick it
        if (!basics.brand && res.data.length === 1) {
          updateBasics({ brand: res.data[0].name, logoUrl: res.data[0].logo ?? null })
        }
      })
      .catch(() =>
        toast({
          title: 'Could not load brands',
          description: 'Refresh and try again.',
          variant: 'destructive',
        }),
      )
      .finally(() => setLoadingBrands(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A campaign that isn't gifting has no product-only option
  useEffect(() => {
    if (basics.dealType === 'content') updateBasics({ dealType: 'gifting' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const pickBrand = (id: string) => {
    const brand = brands.find((b) => b.id === id)
    if (!brand) return
    updateBasics({
      brand: brand.name,
      ...(imageSource === 'brandLogo' ? { logoUrl: brand.logo ?? null } : {}),
    })
  }

  const pickImageSource = (value: 'brandLogo' | 'customPhoto') => {
    setImageSource(value)
    if (value === 'brandLogo') updateBasics({ logoUrl: selectedBrand?.logo ?? null })
    else if (basics.logoUrl === selectedBrand?.logo) updateBasics({ logoUrl: null })
  }

  const onCropped = async (blob: Blob) => {
    setUploading(true)
    try {
      const fileName = `campaign-${Date.now()}.webp`
      const { uploadUrl, publicUrl } = (await brandsApi.getUploadUrl(fileName, 'image/webp')).data
      await brandsApi.uploadFile(uploadUrl, new File([blob], fileName, { type: 'image/webp' }))
      updateBasics({ logoUrl: publicUrl })
    } catch {
      toast({ title: "Couldn't upload the photo", variant: 'destructive' })
    } finally {
      setUploading(false)
      if (cropUrl) URL.revokeObjectURL(cropUrl)
      setCropUrl(null)
    }
  }

  const valid = basics.brand.trim() !== '' && basics.name.trim() !== ''

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (valid) goToNextStep(1)
      }}
      className="mx-auto max-w-3xl space-y-6"
    >
      <div>
        <h2 className="text-2xl font-bold text-foreground sm:text-3xl">Let's get started</h2>
        <p className="mt-1 text-muted-foreground">
          Choose the type of campaign you want to run. You can always adjust details later.
        </p>
      </div>

      {/* Campaign type */}
      <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Campaign type">
        {CAMPAIGN_TYPES.map((t) => {
          const on = type === t.id
          const Icon = t.icon
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => updateBasics({ dealType: t.id })}
              className={cn(
                'relative flex gap-3 rounded-2xl border-2 bg-card p-4 text-left transition-colors sm:flex-col sm:p-5',
                on ? 'border-[#9BD85A] bg-[#F6FDEE]' : 'border-border hover:border-[#C9E9A6]',
              )}
            >
              {on ? (
                <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#111827]">
                  <Check className="h-3 w-3 text-[#4d7c0f]" strokeWidth={3} />
                </span>
              ) : null}
              <span
                className={cn(
                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-full',
                  TONES[t.tone].chip,
                )}
              >
                <Icon className="h-6 w-6" />
              </span>
              <span className="min-w-0 pr-5 sm:pr-0">
                <span className="block font-semibold text-foreground">{t.label}</span>
                <span className="mt-1 block text-sm text-muted-foreground">{t.blurb}</span>
              </span>
            </button>
          )
        })}
      </div>

      <WizardCard
        icon={Package}
        title="Campaign basics"
        subtitle={`Tell us the key details about your ${typeInfo.label.toLowerCase()} campaign.`}
      >
        <div>
          <FieldLabel htmlFor="campaign-name" required>
            Campaign name
          </FieldLabel>
          <Input
            id="campaign-name"
            value={basics.name}
            maxLength={100}
            placeholder="e.g. Holiday gifting"
            onChange={(e) => updateBasics({ name: e.target.value })}
            className="bg-card"
          />
          <Counter value={basics.name} max={100} />
        </div>

        <div>
          <FieldLabel htmlFor="brand" required>
            Select brand
          </FieldLabel>
          <Select
            value={selectedBrand?.id ?? ''}
            onValueChange={pickBrand}
            disabled={loadingBrands || brands.length === 0}
          >
            <SelectTrigger id="brand" className="h-12 bg-card">
              {selectedBrand ? (
                // A div: the trigger clamps direct <span> children, which breaks the row
                <div className="flex items-center gap-2.5">
                  <BrandMark brand={selectedBrand} />
                  <span className="font-medium">{selectedBrand.name}</span>
                </div>
              ) : (
                <SelectValue placeholder={loadingBrands ? 'Loading brands…' : 'Pick a brand'} />
              )}
            </SelectTrigger>
            <SelectContent>
              {brands.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  <span className="flex items-center gap-2">
                    <BrandMark brand={b} size="h-5 w-5 text-xs" />
                    {b.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!loadingBrands && brands.length === 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Add a brand in your brand profile first.
            </p>
          ) : null}
        </div>

        <div>
          <FieldLabel>Campaign photo</FieldLabel>
          <RadioGroup
            value={imageSource}
            onValueChange={(v) => pickImageSource(v as 'brandLogo' | 'customPhoto')}
            className="gap-2"
          >
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <RadioGroupItem value="brandLogo" /> Use brand logo
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <RadioGroupItem value="customPhoto" /> Upload custom campaign photo
            </label>
          </RadioGroup>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) setCropUrl(URL.createObjectURL(f))
              e.target.value = ''
            }}
          />
          <div className="mt-3 flex items-center gap-4 rounded-xl bg-[#F3F4F6] p-4">
            {imageSource === 'brandLogo' ? (
              <>
                <BrandMark brand={selectedBrand} size="h-16 w-16 text-xl" />
                <div className="min-w-0">
                  <p className="font-semibold text-foreground">Brand logo preview</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedBrand?.logo
                      ? 'This will appear on the campaign page for athletes.'
                      : 'This brand has no logo yet, so athletes see its initials.'}
                  </p>
                </div>
              </>
            ) : basics.logoUrl ? (
              <>
                <img src={basics.logoUrl} alt="" className="h-16 w-16 rounded-lg object-cover" />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="bg-card"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Change photo
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="bg-card"
                    onClick={() => updateBasics({ logoUrl: null })}
                  >
                    <X className="h-4 w-4" /> Remove
                  </Button>
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-card py-5 text-sm text-muted-foreground hover:text-foreground"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                Upload a photo (PNG or JPG, at least 300 × 300)
              </button>
            )}
          </div>
          {cropUrl ? (
            <LogoCropModal
              open
              imageUrl={cropUrl}
              onClose={() => {
                URL.revokeObjectURL(cropUrl)
                setCropUrl(null)
              }}
              onCropComplete={onCropped}
            />
          ) : null}
        </div>

        <div>
          <FieldLabel>Campaign duration</FieldLabel>
          <RadioGroup
            value={dateType}
            onValueChange={(v) => updateBasics({ dateType: v as 'exact' | 'timeframe' })}
            className="flex flex-wrap gap-x-6 gap-y-2"
          >
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <RadioGroupItem value="exact" /> Exact dates
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <RadioGroupItem value="timeframe" /> Timeframe (e.g. 30 days)
            </label>
          </RadioGroup>
          {dateType === 'exact' ? (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <DateField
                label="Start date"
                value={basics.startDate}
                onChange={(startDate) => updateBasics({ startDate })}
              />
              <DateField
                label="End date"
                value={basics.endDate}
                min={basics.startDate}
                onChange={(endDate) => updateBasics({ endDate })}
              />
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2">
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                aria-label="Number of days"
                placeholder="30"
                value={basics.timeframeDays ?? ''}
                onChange={(e) =>
                  updateBasics({ timeframeDays: e.target.value ? Number(e.target.value) : null })
                }
                className="w-28 bg-card"
              />
              <span className="text-sm text-muted-foreground">days</span>
            </div>
          )}
        </div>

        <div>
          <FieldLabel optional>Social platforms</FieldLabel>
          <p className="-mt-1 mb-2 text-sm text-muted-foreground">
            Where would you like athletes to post content?
          </p>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => {
              const on = basics.socialPlatform.includes(p.id)
              const Icon = p.icon
              return (
                <Chip
                  key={p.id}
                  on={on}
                  onClick={() =>
                    updateBasics({
                      socialPlatform: on
                        ? basics.socialPlatform.filter((x) => x !== p.id)
                        : [...basics.socialPlatform, p.id],
                    })
                  }
                >
                  <Icon className={cn('h-4 w-4', p.color)} />
                  {p.id}
                </Chip>
              )
            })}
          </div>
        </div>

        <div>
          <FieldLabel htmlFor="budget" optional>
            Budget
          </FieldLabel>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-40">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                $
              </span>
              <Input
                id="budget"
                type="number"
                inputMode="numeric"
                min={0}
                step={100}
                placeholder="1,000"
                value={basics.budget ?? ''}
                onChange={(e) =>
                  updateBasics({ budget: e.target.value ? Number(e.target.value) : null })
                }
                className="bg-card pl-7"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              The total for product, fees and any extra spend.
            </p>
          </div>
        </div>
      </WizardCard>

      <WizardCard
        icon={Trophy}
        title={type === 'gifting' ? 'Performance terms' : 'Pay and applications'}
        subtitle={
          type === 'gifting'
            ? 'What athletes earn on top of the product.'
            : 'What each athlete is paid, and whether they can apply.'
        }
      >
        {type === 'gifting' ? (
          <GiftingBonusEditor
            value={basics.giftingBonuses ?? org?.preferences?.gifting_bonuses ?? null}
            onChange={(tiers) => updateBasics({ giftingBonuses: tiers })}
          />
        ) : (
          <div>
            <FieldLabel htmlFor="payout" optional>
              {type === 'licensing' ? 'Pay per athlete, per month' : 'Pay per athlete'}
            </FieldLabel>
            <div className="relative w-40">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                $
              </span>
              <Input
                id="payout"
                type="number"
                inputMode="numeric"
                min={0}
                step={50}
                value={basics.payout ?? ''}
                onChange={(e) =>
                  updateBasics({ payout: e.target.value ? Number(e.target.value) : null })
                }
                className="bg-card pl-7"
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Leave blank to use each athlete's own prices when you book.
            </p>
          </div>
        )}

        <div className="flex items-start justify-between gap-4 border-t border-border pt-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Let athletes apply</p>
            <p className="text-sm text-muted-foreground">
              Athletes who fit see it in their Marketplace and apply; you accept, counter or pass.
              Off means invite only.
            </p>
          </div>
          <Switch
            checked={basics.openToApplications}
            onCheckedChange={(on) => updateBasics({ openToApplications: on })}
            aria-label="Let athletes apply"
          />
        </div>
        {basics.openToApplications ? (
          <div className="max-w-xs">
            <FieldLabel htmlFor="deadline" optional>
              Applications close
            </FieldLabel>
            <Input
              id="deadline"
              type="date"
              value={basics.applicationDeadline ?? ''}
              onChange={(e) => updateBasics({ applicationDeadline: e.target.value || null })}
              className="bg-card"
            />
          </div>
        ) : null}
      </WizardCard>

      <WizardFooter primaryLabel="Continue" primaryDisabled={!valid} submit />
    </form>
  )
}

export default Step1Basics
